import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { PageHeader } from './ui';
import {
  User,
  CreditCard,
  Crown,
  Check,
  Bell,
  Sun,
  Moon,
  Monitor,
  IdCard,
  Receipt,
  ExternalLink,
  Download,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import API_URL from '../config';
import { getSubscriptionTier, isFreeSubscription, mergeSubscriptionTier } from '../utils/subscription';
import {
  planMonthlyBadge,
  getDriverLimit,
  isFleetPlanTier,
  FLEET_BASE_MONTHLY_DOLLARS,
  FLEET_PER_VEHICLE_MONTHLY_DOLLARS,
  fleetMonthlyTotalDollars,
} from '../config/pricingPublic';
import { showSuccess, showError } from '../utils/toast';
import '../App.css';
import '../pages/legal-pages.css';

const FLEET_TRUCKS_MAX = 25;

const CANCEL_REASON_OPTIONS = [
  { value: 'too_expensive', label: 'It costs too much' },
  { value: 'missing_feature', label: "It's missing a feature I need" },
  { value: 'not_using', label: "I'm not using it enough" },
  { value: 'switching_service', label: "I'm switching to another service" },
  { value: 'closing_business', label: "I'm closing or pausing my business" },
  { value: 'hard_to_use', label: "It's too hard to use" },
  { value: 'other', label: 'Something else' },
];

const PLAN_FEATURES_FREE = [
  'Compliance dashboard',
  'Document storage',
  'Vehicle tracking (1-2 vehicles)',
  'Authority lookup',
  'Basic alerts',
  'No driver management',
];

const PLAN_FEATURES_OWNER_OPERATOR = [
  'Compliance Intelligence Dashboard',
  'Audit Readiness Score + Timeline',
  'Vehicle Readiness Score',
  'Document expiration alerts',
  'IFTA tracking',
  'Authority compliance monitoring',
  'Rynn AI compliance assistant',
  'Up to 2 drivers',
];

const PLAN_FEATURES_FLEET = [
  'Everything in Owner-Operator',
  'Up to 25 vehicles',
  'Multi-driver management',
  'Up to 25 drivers',
  'SMS alerts',
  'Advanced compliance reporting',
  'Fleet-level analytics',
  'Priority support',
];

function planFeaturesForUser(user) {
  if (!user || isFreeSubscription(user)) {
    return PLAN_FEATURES_FREE;
  }
  const t = getSubscriptionTier(user);
  if (t === 'fleet' || t === 'pro') {
    return PLAN_FEATURES_FLEET;
  }
  if (t === 'owner-operator' || t === 'owner_operator') {
    return PLAN_FEATURES_OWNER_OPERATOR;
  }
  if (t === 'basic') {
    return PLAN_FEATURES_OWNER_OPERATOR;
  }
  return PLAN_FEATURES_FREE;
}

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return { headers: { Authorization: token ? `Bearer ${token}` : '' } };
}

function formatDeletionDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function refreshUserFromApi(setUser) {
  const token = localStorage.getItem('token');
  if (!token) return Promise.resolve();
  return axios
    .get(`${API_URL}/api/auth/profile`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => {
      if (r.data) {
        const merged = mergeSubscriptionTier(r.data);
        setUser(merged);
        try {
          localStorage.setItem('user', JSON.stringify(merged));
        } catch {
          /* ignore quota */
        }
      }
    });
}

export default function Settings() {
  const navigate = useNavigate();
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [user, setUser] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [prefsLoading, setPrefsLoading] = useState(true);
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [verifyPhoneMessage, setVerifyPhoneMessage] = useState(null);
  const [accountPhone, setAccountPhone] = useState('');
  const [accountPhoneSaving, setAccountPhoneSaving] = useState(false);
  const [cpPassport, setCpPassport] = useState(null);
  const [cpLoading, setCpLoading] = useState(true);
  const [cpBusy, setCpBusy] = useState(false);

  const [changePwCurrent, setChangePwCurrent] = useState('');
  const [changePwNew, setChangePwNew] = useState('');
  const [changePwConfirm, setChangePwConfirm] = useState('');
  const [changePwLoading, setChangePwLoading] = useState(false);
  const [changePwError, setChangePwError] = useState('');
  const [changePwSuccess, setChangePwSuccess] = useState('');
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingInvoices, setBillingInvoices] = useState([]);
  const [billingError, setBillingError] = useState(false);
  const [driverCount, setDriverCount] = useState(0);
  const [activeVehicleCount, setActiveVehicleCount] = useState(null);
  const [trackedTrucks, setTrackedTrucks] = useState(null);
  const [truckQty, setTruckQty] = useState(1);
  const [trucksUpdating, setTrucksUpdating] = useState(false);
  const [subStatus, setSubStatus] = useState(null);
  const [trialEndsAt, setTrialEndsAt] = useState(null);
  const [trialCode, setTrialCode] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelComment, setCancelComment] = useState('');
  const [resumeLoading, setResumeLoading] = useState(false);

  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [deletionLoading, setDeletionLoading] = useState(false);
  const [deletionError, setDeletionError] = useState('');

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        setUser(mergeSubscriptionTier(JSON.parse(userData)));
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }
    const token = localStorage.getItem('token');
    if (!token) return;
    axios
      .get(`${API_URL}/api/auth/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (r.data) {
          const merged = mergeSubscriptionTier(r.data);
          setUser(merged);
          try {
            localStorage.setItem('user', JSON.stringify(merged));
          } catch {
            /* ignore quota */
          }
        }
      })
      .catch(() => {
        /* keep local user */
      });
  }, []);

  useEffect(() => {
    if (!user || isFreeSubscription(user)) {
      setBillingInvoices([]);
      setBillingError(false);
      setBillingLoading(false);
      return;
    }
    let cancelled = false;
    const loadBilling = async () => {
      setBillingLoading(true);
      setBillingError(false);
      try {
        const res = await axios.get(
          `${API_URL}/api/subscriptions/billing-history`,
          getAuthHeaders(),
        );
        if (!cancelled) {
          setBillingInvoices(Array.isArray(res.data?.invoices) ? res.data.invoices : []);
        }
      } catch (err) {
        if (!cancelled) {
          setBillingError(true);
          setBillingInvoices([]);
        }
      } finally {
        if (!cancelled) setBillingLoading(false);
      }
    };
    loadBilling();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    let cancelled = false;
    axios
      .get(`${API_URL}/api/drivers`, getAuthHeaders())
      .then((res) => {
        if (!cancelled) {
          setDriverCount(Array.isArray(res.data) ? res.data.length : 0);
        }
      })
      .catch(() => {
        if (!cancelled) setDriverCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    const tier = user ? getSubscriptionTier(user) : 'free';
    if (!isFleetPlanTier(tier)) {
      setActiveVehicleCount(null);
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) return;
    let cancelled = false;
    axios
      .get(`${API_URL}/api/vehicles`, getAuthHeaders())
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res.data) ? res.data : [];
        setActiveVehicleCount(rows.filter((v) => v?.isActive !== false).length);
      })
      .catch(() => {
        if (!cancelled) setActiveVehicleCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!user || isFreeSubscription(user)) {
      setTrackedTrucks(null);
      setSubStatus(null);
      setTrialEndsAt(null);
      setTrialCode(null);
      setCancelAtPeriodEnd(false);
      setCurrentPeriodEnd(null);
      return;
    }
    const isFleet = isFleetPlanTier(getSubscriptionTier(user));
    if (!isFleet) {
      setTrackedTrucks(null);
    }
    let cancelled = false;
    axios
      .get(`${API_URL}/api/subscriptions/status`, getAuthHeaders())
      .then((res) => {
        if (cancelled) return;
        const data = res.data || {};
        setSubStatus(data.status ?? null);
        setTrialEndsAt(data.trialEndsAt ?? null);
        setTrialCode(data.trialCode ?? null);
        setCancelAtPeriodEnd(data.cancelAtPeriodEnd === true);
        setCurrentPeriodEnd(data.currentPeriodEnd ?? null);
        if (!isFleet) return;
        if (Number.isFinite(Number(data.activeVehicleCount))) {
          setActiveVehicleCount(Number(data.activeVehicleCount));
        }
        const rawTracked = Number(data.trackedTrucks);
        const tracked =
          Number.isInteger(rawTracked) && rawTracked >= 1 ? rawTracked : null;
        setTrackedTrucks(tracked);
        const active = Number.isFinite(Number(data.activeVehicleCount))
          ? Number(data.activeVehicleCount)
          : 0;
        const initial = tracked ?? Math.max(1, active);
        setTruckQty(Math.min(FLEET_TRUCKS_MAX, Math.max(1, initial)));
      })
      .catch(() => {
        /* keep existing vehicle count */
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    const loadProfilePhone = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/users/profile`, getAuthHeaders());
        setAccountPhone(res.data?.phone || '');
      } catch (err) {
        console.error('Error loading profile phone:', err);
      }
    };
    loadProfilePhone();
  }, []);

  useEffect(() => {
    const fetchPrefs = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/notifications/preferences`, getAuthHeaders());
        setPrefs(res.data);
      } catch (err) {
        console.error('Error fetching notification preferences:', err);
        setPrefs({
          email_enabled: true,
          sms_enabled: false,
          phone: '',
          lead_time_days: 7,
          quiet_hours: { start: '22:00', end: '07:00' },
        });
      } finally {
        setPrefsLoading(false);
      }
    };
    fetchPrefs();
  }, []);

  const loadCompliancePassport = async () => {
    setCpLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/passport/me`, getAuthHeaders());
      setCpPassport(res.data?.passport ?? null);
    } catch {
      setCpPassport(null);
    } finally {
      setCpLoading(false);
    }
  };

  useEffect(() => {
    loadCompliancePassport();
  }, []);

  const saveAccountMobile = async (value) => {
    const v = (value ?? '').trim();
    setAccountPhoneSaving(true);
    try {
      await axios.put(`${API_URL}/api/users/profile`, { phone: v }, getAuthHeaders());
      setAccountPhone(v);
      setPrefs((p) => (p ? { ...p, phone: v } : p));
      const raw = localStorage.getItem('user');
      if (raw) {
        try {
          const u = JSON.parse(raw);
          u.phone = v || null;
          localStorage.setItem('user', JSON.stringify(u));
        } catch {
          /* ignore */
        }
      }
    } catch (err) {
      console.error('Error saving mobile number:', err);
    } finally {
      setAccountPhoneSaving(false);
    }
  };

  const saveNotificationPrefs = async (updates) => {
    if (!prefs) return;
    setPrefsSaving(true);
    try {
      const payload = {
        email_enabled: updates.email_enabled ?? prefs.email_enabled,
        sms_enabled: updates.sms_enabled ?? prefs.sms_enabled,
        phone: updates.phone !== undefined ? updates.phone : prefs.phone,
        lead_time_days: updates.lead_time_days !== undefined ? updates.lead_time_days : prefs.lead_time_days,
        quiet_hours_start: updates.quiet_hours_start ?? prefs.quiet_hours?.start,
        quiet_hours_end: updates.quiet_hours_end ?? prefs.quiet_hours?.end,
      };
      const res = await axios.patch(`${API_URL}/api/notifications/preferences`, payload, getAuthHeaders());
      setPrefs(res.data);
      if (payload.phone !== undefined) {
        setAccountPhone((payload.phone || '').trim());
      }
    } catch (err) {
      console.error('Error saving notification preferences:', err);
    } finally {
      setPrefsSaving(false);
    }
  };

  const handleVerifyPhone = () => {
    if (!prefs?.phone?.trim()) {
      setVerifyPhoneMessage('Enter a phone number first.');
      return;
    }
    setVerifyPhoneMessage('Verification not implemented yet. Your number is saved.');
    setTimeout(() => setVerifyPhoneMessage(null), 3000);
  };

  const currentPlan = user ? getSubscriptionTier(user) : 'free';
  const driverLimit = getDriverLimit(currentPlan);
  const driverUsagePct =
    driverLimit > 0 ? Math.min(100, Math.round((driverCount / driverLimit) * 100)) : 0;
  const atDriverLimit = driverLimit > 0 && driverCount >= driverLimit;
  const showFleetTrucks =
    Boolean(user) && !isFreeSubscription(user) && isFleetPlanTier(currentPlan);
  const planStatus = String(
    subStatus || user?.subscription?.status || '',
  ).toLowerCase();
  const showTrialBanner = planStatus === 'trialing' && Boolean(trialEndsAt);
  const showCancelPlan =
    Boolean(user) && !isFreeSubscription(user) && planStatus !== 'canceled';
  const addedTruckCount = Number.isFinite(Number(activeVehicleCount))
    ? Number(activeVehicleCount)
    : 0;
  const minTruckQty = Math.max(1, addedTruckCount);
  const displayTruckQty = Math.min(
    FLEET_TRUCKS_MAX,
    Math.max(minTruckQty, truckQty),
  );
  const savedTruckQty = trackedTrucks != null ? trackedTrucks : minTruckQty;
  const trucksUnchanged = displayTruckQty === savedTruckQty;

  const refreshBillingHistory = async () => {
    if (!user || isFreeSubscription(user)) return;
    try {
      const res = await axios.get(
        `${API_URL}/api/subscriptions/billing-history`,
        getAuthHeaders(),
      );
      setBillingInvoices(Array.isArray(res.data?.invoices) ? res.data.invoices : []);
      setBillingError(false);
    } catch {
      setBillingError(true);
    }
  };

  const refreshSubscriptionStatus = async () => {
    const res = await axios.get(
      `${API_URL}/api/subscriptions/status`,
      getAuthHeaders(),
    );
    const data = res.data || {};
    if (Number.isFinite(Number(data.activeVehicleCount))) {
      setActiveVehicleCount(Number(data.activeVehicleCount));
    }
    const rawTracked = Number(data.trackedTrucks);
    const tracked =
      Number.isInteger(rawTracked) && rawTracked >= 1 ? rawTracked : null;
    setTrackedTrucks(tracked);
    setSubStatus(data.status ?? null);
    setTrialEndsAt(data.trialEndsAt ?? null);
    setTrialCode(data.trialCode ?? null);
    setCancelAtPeriodEnd(data.cancelAtPeriodEnd === true);
    setCurrentPeriodEnd(data.currentPeriodEnd ?? null);
    const active = Number.isFinite(Number(data.activeVehicleCount))
      ? Number(data.activeVehicleCount)
      : addedTruckCount;
    const next = tracked ?? Math.max(1, active);
    setTruckQty(Math.min(FLEET_TRUCKS_MAX, Math.max(1, next)));
  };

  const handleCancelPlan = async () => {
    if (cancelLoading) return;
    setCancelLoading(true);
    try {
      const body = {};
      if (cancelReason) body.reason = cancelReason;
      if (cancelComment.trim()) body.comment = cancelComment.trim();
      const { data } = await axios.post(
        `${API_URL}/api/subscriptions/cancel`,
        body,
        getAuthHeaders(),
      );
      try {
        const res = await axios.get(
          `${API_URL}/api/subscriptions/billing-history`,
          getAuthHeaders(),
        );
        setBillingInvoices(Array.isArray(res.data?.invoices) ? res.data.invoices : []);
        setBillingError(false);
      } catch {
        setBillingError(true);
      }
      if (data?.cancelAtPeriodEnd) {
        setCancelAtPeriodEnd(true);
        setCurrentPeriodEnd(data.currentPeriodEnd ?? null);
        setShowCancelConfirm(false);
        setCancelReason('');
        setCancelComment('');
        const endDate = formatDeletionDate(data.currentPeriodEnd);
        showSuccess(
          endDate
            ? `Your plan will end on ${endDate}. You'll keep full access until then.`
            : "Your plan will end at the end of your current billing period. You'll keep full access until then.",
        );
        return;
      }
      const next = mergeSubscriptionTier({
        ...user,
        subscription: {
          ...user.subscription,
          tier: 'free',
          status: 'canceled',
          stripeSubscriptionId: undefined,
        },
      });
      setUser(next);
      try {
        localStorage.setItem('user', JSON.stringify(next));
      } catch {
        /* ignore quota */
      }
      setSubStatus('canceled');
      setTrialEndsAt(null);
      setTrialCode(null);
      setTrackedTrucks(null);
      setCancelAtPeriodEnd(false);
      setCurrentPeriodEnd(null);
      setShowCancelConfirm(false);
      setCancelReason('');
      setCancelComment('');
      showSuccess("Your plan has been canceled. You're now on the Free plan.");
    } catch (err) {
      const msg = err.response?.data?.message;
      showError(
        Array.isArray(msg) ? msg[0] : msg || 'Could not cancel your plan. Try again.',
      );
    } finally {
      setCancelLoading(false);
    }
  };

  const handleResumePlan = async () => {
    if (resumeLoading) return;
    setResumeLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/subscriptions/resume`,
        {},
        getAuthHeaders(),
      );
      setCancelAtPeriodEnd(false);
      showSuccess('Your plan will continue as normal.');
    } catch (err) {
      const msg = err.response?.data?.message;
      showError(
        Array.isArray(msg) ? msg[0] : msg || 'Could not keep your plan. Try again.',
      );
    } finally {
      setResumeLoading(false);
    }
  };

  const handleUpdateTrucks = async () => {
    if (trucksUnchanged || trucksUpdating) return;
    setTrucksUpdating(true);
    try {
      await axios.post(
        `${API_URL}/api/subscriptions/update-trucks`,
        { quantity: displayTruckQty },
        getAuthHeaders(),
      );
      showSuccess(
        `Your plan now covers ${displayTruckQty} trucks. Changes are prorated on your next invoice.`,
      );
      await refreshSubscriptionStatus();
      await refreshBillingHistory();
    } catch (err) {
      const msg = err.response?.data?.message;
      showError(
        Array.isArray(msg) ? msg[0] : msg || 'Could not update your truck count.',
      );
    } finally {
      setTrucksUpdating(false);
    }
  };

  const formatInvoiceDate = (unixTimestamp) => {
    return new Date(unixTimestamp * 1000)
      .toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
  };

  const formatInvoiceAmount = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency?.toUpperCase() || 'USD'
    }).format(amount);
  };

  const invoiceStatusDisplay = (status) => {
    switch(status) {
      case 'paid':
        return { label: 'Paid', className: 'badge-success' };
      case 'open':
        return { label: 'Due', className: 'badge-warning' };
      case 'void':
        return { label: 'Void', className: 'badge-void' };
      case 'uncollectible':
        return { label: 'Failed', className: 'badge-danger' };
      default:
        return { label: status, className: 'badge-void' };
    }
  };

  return (
    <div className="settings-container page-container">
      <PageHeader title="Settings" subtitle="Manage your account, subscription, and notification preferences" />

      <div className="settings-sections">
        {/* Appearance / Theme */}
        <div className="settings-section card">
          <div className="section-header">
            <Sun size={24} color="var(--accent)" />
            <h2>Appearance</h2>
          </div>
          <p className="settings-section-desc">Choose how Sovrynn looks. System follows your device setting.</p>
          <div className="theme-toggle">
            <label className="theme-option">
              <input
                type="radio"
                name="theme"
                value="light"
                checked={theme === 'light'}
                onChange={(e) => setTheme(e.target.value)}
              />
              <span className="theme-option-label"><Sun size={18} /> Light</span>
            </label>
            <label className="theme-option">
              <input
                type="radio"
                name="theme"
                value="dark"
                checked={theme === 'dark'}
                onChange={(e) => setTheme(e.target.value)}
              />
              <span className="theme-option-label"><Moon size={18} /> Dark</span>
            </label>
            <label className="theme-option">
              <input
                type="radio"
                name="theme"
                value="system"
                checked={theme === 'system'}
                onChange={(e) => setTheme(e.target.value)}
              />
              <span className="theme-option-label"><Monitor size={18} /> System</span>
            </label>
          </div>
          {theme === 'system' && (
            <p className="settings-muted" style={{ marginTop: 8 }}>Using {resolvedTheme} mode from your device.</p>
          )}
        </div>

        {/* Billing History */}
        <div className="settings-section card billing-history-section">
          <div className="section-header">
            <Receipt size={24} color="#E09B1A" />
            <h2>Billing History</h2>
          </div>

          {!user ? (
            <p className="settings-muted">Loading billing history...</p>
          ) : isFreeSubscription(user) ? (
            <div className="billing-history-free-state">
              <p className="settings-muted" style={{ textAlign: 'center', marginBottom: 16 }}>
                Billing history is available for paid subscribers. Upgrade to Owner-Operator to start your
                compliance journey.
              </p>
              <div style={{ textAlign: 'center' }}>
                <button type="button" className="button" onClick={() => navigate('/pricing')}>
                  Upgrade Now
                </button>
              </div>
            </div>
          ) : billingLoading ? (
            <p className="settings-muted">Loading billing history...</p>
          ) : billingError ? (
            <p className="settings-muted">Unable to load billing history. Please try again later.</p>
          ) : billingInvoices.length === 0 ? (
            <p className="settings-muted" style={{ textAlign: 'center' }}>
              No billing history yet. Invoices will appear here after your first payment.
            </p>
          ) : (
            <>
              <div className="billing-history-desktop">
                <div className="billing-history-table-wrap">
                  <table className="billing-history-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Description</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Invoice</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billingInvoices.map((inv) => {
                        const st = invoiceStatusDisplay(inv.status);
                        return (
                          <tr key={inv.id}>
                            <td>{formatInvoiceDate(inv.date)}</td>
                            <td>{inv.description || 'Sovrynn Subscription'}</td>
                            <td>{formatInvoiceAmount(inv.amount, inv.currency)}</td>
                            <td>
                              <span className={st.className}>{st.label}</span>
                            </td>
                            <td>
                              <div className="billing-invoice-links">
                                {inv.invoiceUrl ? (
                                  <a
                                    href={inv.invoiceUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="View invoice"
                                    aria-label="View invoice"
                                  >
                                    <ExternalLink size={20} strokeWidth={2} />
                                  </a>
                                ) : null}
                                {inv.pdfUrl ? (
                                  <a
                                    href={inv.pdfUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Download PDF"
                                    aria-label="Download PDF"
                                  >
                                    <Download size={20} strokeWidth={2} />
                                  </a>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="billing-history-mobile">
                {billingInvoices.map((inv) => {
                  const st = invoiceStatusDisplay(inv.status);
                  return (
                    <div key={inv.id} className="billing-history-card">
                      <div className="billing-history-card__row">
                        <span className="billing-history-card__label">Date</span>
                        <span>{formatInvoiceDate(inv.date)}</span>
                      </div>
                      <div className="billing-history-card__row billing-history-card__row--block">
                        <span className="billing-history-card__label">Description</span>
                        <span className="billing-history-card__value">{inv.description || 'Sovrynn Subscription'}</span>
                      </div>
                      <div className="billing-history-card__row">
                        <span className="billing-history-card__label">Amount</span>
                        <span>{formatInvoiceAmount(inv.amount, inv.currency)}</span>
                      </div>
                      <div className="billing-history-card__row">
                        <span className="billing-history-card__label">Status</span>
                        <span className={st.className}>{st.label}</span>
                      </div>
                      <div className="billing-history-card__row">
                        <span className="billing-history-card__label">Invoice</span>
                        <div className="billing-invoice-links">
                          {inv.invoiceUrl ? (
                            <a
                              href={inv.invoiceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="View invoice"
                              aria-label="View invoice"
                            >
                              <ExternalLink size={20} strokeWidth={2} />
                            </a>
                          ) : null}
                          {inv.pdfUrl ? (
                            <a
                              href={inv.pdfUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Download PDF"
                              aria-label="Download PDF"
                            >
                              <Download size={20} strokeWidth={2} />
                            </a>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Plan Section */}
        <div className="settings-section card">
          <div className="section-header">
            <Crown size={24} color="#E09B1A" />
            <h2>Subscription Plan</h2>
          </div>
          
          <div className="current-plan">
            <div className="plan-badge">
              <span
                className={`status-badge ${
                  currentPlan === 'free' ? '' : 'excellent'
                }`}
              >
                {planMonthlyBadge(
                  currentPlan,
                  isFleetPlanTier(currentPlan) ? activeVehicleCount : undefined,
                )}
              </span>
            </div>
            {user && !isFreeSubscription(user) && (
              <p className="settings-muted" style={{ marginTop: 12 }}>
                Your exact subscription amount is shown on Stripe invoices in Billing History above.
              </p>
            )}
            {showTrialBanner && (
              <p className="settings-muted" style={{ marginTop: 12 }}>
                Free trial{trialCode ? ` (${trialCode})` : ''}: ends{' '}
                {formatDeletionDate(trialEndsAt)}. You'll be billed monthly after
                that unless you cancel.
              </p>
            )}
            {showCancelPlan && cancelAtPeriodEnd && (
              <div style={{ marginTop: 16 }}>
                <p className="settings-muted" style={{ marginTop: 0 }}>
                  {formatDeletionDate(currentPeriodEnd)
                    ? `Your plan ends on ${formatDeletionDate(currentPeriodEnd)}.`
                    : 'Your plan ends at the end of your current billing period.'}
                </p>
                <button
                  type="button"
                  className="button"
                  disabled={resumeLoading}
                  onClick={handleResumePlan}
                  style={{ marginTop: 12 }}
                >
                  {resumeLoading ? 'Keeping plan…' : 'Keep my plan'}
                </button>
              </div>
            )}
            {showCancelPlan && !cancelAtPeriodEnd && (
              <div style={{ marginTop: 16 }}>
                {!showCancelConfirm ? (
                  <button
                    type="button"
                    className="button secondary"
                    onClick={() => {
                      setCancelReason('');
                      setCancelComment('');
                      setShowCancelConfirm(true);
                    }}
                  >
                    Cancel plan
                  </button>
                ) : (
                  <div>
                    <p className="settings-muted" style={{ marginTop: 0 }}>
                      {planStatus === 'trialing'
                        ? "Cancel your free trial? You'll lose paid features right away and move to the Free plan. You won't be charged."
                        : formatDeletionDate(currentPeriodEnd)
                          ? `Cancel your plan? You'll keep full access until ${formatDeletionDate(currentPeriodEnd)}, then your account moves to Free. You won't be charged again. Your data stays.`
                          : "Cancel your plan? You'll keep full access until the end of your current billing period, then your account moves to Free. You won't be charged again. Your data stays."}
                    </p>
                    <div style={{ marginTop: 16 }}>
                      <label className="setting-label" htmlFor="settings-cancel-reason">
                        Mind telling us why? (optional)
                      </label>
                      <select
                        id="settings-cancel-reason"
                        className="input-field"
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        disabled={cancelLoading}
                      >
                        <option value="" />
                        {CANCEL_REASON_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <textarea
                        id="settings-cancel-comment"
                        className="input-field"
                        aria-label="Tell us more (optional)"
                        value={cancelComment}
                        onChange={(e) => setCancelComment(e.target.value.slice(0, 500))}
                        maxLength={500}
                        rows={3}
                        disabled={cancelLoading}
                        style={{ marginTop: 8, resize: 'vertical' }}
                      />
                      <p className="settings-muted" style={{ marginTop: 4 }}>
                        {cancelComment.length}/500
                      </p>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                      <button
                        type="button"
                        className="button"
                        disabled={cancelLoading}
                        onClick={handleCancelPlan}
                      >
                        {cancelLoading ? 'Canceling…' : 'Yes, cancel plan'}
                      </button>
                      <button
                        type="button"
                        className="button secondary"
                        disabled={cancelLoading}
                        onClick={() => setShowCancelConfirm(false)}
                      >
                        Keep my plan
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {driverLimit > 0 && (
              <div className="settings-driver-usage">
                <div className="settings-driver-usage__header">
                  <span className="settings-driver-usage__label">
                    Drivers: {driverCount} of {driverLimit} used
                  </span>
                  {atDriverLimit && (
                    <span className="settings-driver-usage__limit-reached">Driver limit reached</span>
                  )}
                </div>
                <div className="settings-driver-usage__track" aria-hidden>
                  <div
                    className="settings-driver-usage__fill"
                    style={{ width: `${driverUsagePct}%` }}
                  />
                </div>
              </div>
            )}
            
            {showFleetTrucks && (
              <div className="settings-trucks-plan">
                <h3 className="settings-trucks-plan__title">Trucks on your plan</h3>
                <div className="settings-trucks-plan__stepper">
                  <button
                    type="button"
                    className="button secondary"
                    aria-label="Fewer trucks"
                    disabled={trucksUpdating || displayTruckQty <= minTruckQty}
                    onClick={() =>
                      setTruckQty((n) => Math.max(minTruckQty, n - 1))
                    }
                  >
                    −
                  </button>
                  <span
                    className="settings-trucks-plan__qty"
                    aria-live="polite"
                  >
                    {displayTruckQty}
                  </span>
                  <button
                    type="button"
                    className="button secondary"
                    aria-label="More trucks"
                    disabled={trucksUpdating || displayTruckQty >= FLEET_TRUCKS_MAX}
                    onClick={() =>
                      setTruckQty((n) => Math.min(FLEET_TRUCKS_MAX, n + 1))
                    }
                  >
                    +
                  </button>
                </div>
                <p className="settings-trucks-plan__price">
                  ${FLEET_BASE_MONTHLY_DOLLARS} base + {displayTruckQty} trucks × $
                  {FLEET_PER_VEHICLE_MONTHLY_DOLLARS} = $
                  {fleetMonthlyTotalDollars(displayTruckQty)}/mo
                </p>
                <p className="settings-trucks-plan__added">
                  Trucks added: {addedTruckCount} of {displayTruckQty}
                </p>
                <button
                  type="button"
                  className="button"
                  disabled={trucksUnchanged || trucksUpdating}
                  onClick={handleUpdateTrucks}
                >
                  {trucksUpdating ? 'Updating…' : 'Update trucks'}
                </button>
                <p className="settings-trucks-plan__hint">
                  Adding a truck on the Vehicles page also raises this automatically. You can only go lower than your added trucks by removing trucks first.
                </p>
              </div>
            )}

            {currentPlan === 'free' && (
              <div className="upgrade-cta">
                <p>Unlock unlimited features with a paid plan</p>
                <button className="button" onClick={() => navigate('/pricing')}>
                  Upgrade Now
                </button>
              </div>
            )}

            <div className="plan-features">
              <h3>Active Features:</h3>
              <ul>
                {planFeaturesForUser(user).map((label) => (
                  <li key={label}>
                    <Check size={18} color="#10B981" aria-hidden />
                    <span>{label}</span>
                  </li>
                ))}
              </ul>
              {!isFreeSubscription(user) && !isFleetPlanTier(currentPlan) && (
                <button className="button secondary" onClick={() => navigate('/pricing')}>
                  Manage Subscription
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Account Section */}
        <div className="settings-section card">
          <div className="section-header">
            <User size={24} color="#E09B1A" />
            <h2>Account</h2>
          </div>
          
          <div className="account-info">
            <div className="setting-item">
              <div>
                <h3>Email</h3>
                <p>{user?.email || 'user@sovrynn.com'}</p>
              </div>
              <button className="button secondary">Change Email</button>
            </div>
            
            <div className="setting-item" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 12 }}>
              <div>
                <h3>Change Password</h3>
                <p className="settings-muted" style={{ marginTop: 4 }}>
                  Enter your current password, then choose a new one (at least 8 characters).
                </p>
              </div>
              <label className="setting-label" htmlFor="settings-cp-current">
                Current Password
              </label>
              <input
                id="settings-cp-current"
                type="password"
                className="input-field"
                value={changePwCurrent}
                onChange={(e) => {
                  setChangePwCurrent(e.target.value);
                  setChangePwError('');
                  setChangePwSuccess('');
                }}
                autoComplete="current-password"
              />
              <label className="setting-label" htmlFor="settings-cp-new">
                New Password
              </label>
              <input
                id="settings-cp-new"
                type="password"
                className="input-field"
                value={changePwNew}
                onChange={(e) => {
                  setChangePwNew(e.target.value);
                  setChangePwError('');
                  setChangePwSuccess('');
                }}
                placeholder="Min 8 characters"
                autoComplete="new-password"
                minLength={8}
              />
              <label className="setting-label" htmlFor="settings-cp-confirm">
                Confirm New Password
              </label>
              <input
                id="settings-cp-confirm"
                type="password"
                className="input-field"
                value={changePwConfirm}
                onChange={(e) => {
                  setChangePwConfirm(e.target.value);
                  setChangePwError('');
                  setChangePwSuccess('');
                }}
                autoComplete="new-password"
                minLength={8}
              />
              {changePwError ? (
                <p className="settings-inline-error" style={{ color: '#DC2626', fontSize: '0.875rem', margin: 0 }}>
                  {changePwError}
                </p>
              ) : null}
              {changePwSuccess ? (
                <p className="settings-inline-success" style={{ color: '#16A34A', fontSize: '0.875rem', margin: 0 }}>
                  {changePwSuccess}
                </p>
              ) : null}
              <button
                type="button"
                className="button"
                disabled={changePwLoading}
                onClick={async () => {
                  setChangePwError('');
                  setChangePwSuccess('');
                  if (changePwNew.length < 8) {
                    setChangePwError('New password must be at least 8 characters.');
                    return;
                  }
                  if (changePwNew !== changePwConfirm) {
                    setChangePwError('New passwords do not match.');
                    return;
                  }
                  setChangePwLoading(true);
                  try {
                    await axios.patch(
                      `${API_URL}/api/auth/change-password`,
                      { currentPassword: changePwCurrent, newPassword: changePwNew },
                      getAuthHeaders(),
                    );
                    setChangePwSuccess('Password updated.');
                    setChangePwCurrent('');
                    setChangePwNew('');
                    setChangePwConfirm('');
                  } catch (err) {
                    let msg = err.response?.data?.message;
                    if (Array.isArray(msg)) msg = msg.join(' ');
                    setChangePwError(msg || 'Could not update password. Try again.');
                  } finally {
                    setChangePwLoading(false);
                  }
                }}
              >
                {changePwLoading ? 'Updating...' : 'Update Password'}
              </button>
            </div>

            <div className="setting-item" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
              <label className="setting-label" htmlFor="settings-account-mobile">
                Mobile Number
              </label>
              <input
                id="settings-account-mobile"
                type="tel"
                className="input-field"
                placeholder="+1 (555) 000-0000"
                value={accountPhone}
                onChange={(e) => setAccountPhone(e.target.value)}
                onBlur={(e) => saveAccountMobile(e.target.value)}
                autoComplete="tel"
              />
              <span className="setting-hint">
                Used for critical compliance SMS alerts only. Standard messaging rates apply.
              </span>
              {accountPhoneSaving && <span className="settings-muted">Saving…</span>}
            </div>
          </div>
        </div>

        {/* Compliance Passport™ */}
        <div className="settings-section card">
          <div className="section-header">
            <IdCard size={24} color="#E09B1A" />
            <h2>Compliance Passport™</h2>
          </div>
          <p className="settings-section-desc">
            Your shareable compliance credential. Deactivating invalidates the current public link.
          </p>
          {cpLoading ?
            <p className="settings-muted">Loading passport status…</p>
          : <>
              {!cpPassport && <p className="settings-muted">No active passport. Generate one from the dashboard or below.</p>}
              {cpPassport && (
                <div className="account-info" style={{ marginBottom: 16 }}>
                  <p>
                    <strong>Status:</strong>{' '}
                    {cpPassport.isExpired ? 'Expired' : cpPassport.passportId ? 'Active' : 'None'}
                  </p>
                  {cpPassport.passportId && (
                    <p>
                      <strong>Passport ID:</strong> {cpPassport.passportId}
                    </p>
                  )}
                  {cpPassport.expiresAt && (
                    <p>
                      <strong>Expires:</strong>{' '}
                      {new Date(cpPassport.expiresAt).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </p>
                  )}
                  {!cpPassport.isExpired && cpPassport.passportId && (
                    <p>
                      <strong>Views:</strong> {cpPassport.viewCount ?? 0}
                    </p>
                  )}
                </div>
              )}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <button
                  type="button"
                  className="button secondary"
                  disabled={cpBusy || !cpPassport?.passportId}
                  onClick={() => {
                    if (
                      !window.confirm(
                        'Are you sure? Anyone with your current link will no longer be able to view it.',
                      )
                    ) {
                      return;
                    }
                    setCpBusy(true);
                    axios
                      .delete(`${API_URL}/api/passport/deactivate`, getAuthHeaders())
                      .then(() => loadCompliancePassport())
                      .finally(() => setCpBusy(false));
                  }}
                >
                  Deactivate Passport
                </button>
                <button
                  type="button"
                  className="button"
                  disabled={cpBusy}
                  onClick={() => {
                    setCpBusy(true);
                    axios
                      .post(`${API_URL}/api/passport/generate`, {}, getAuthHeaders())
                      .then(() => loadCompliancePassport())
                      .finally(() => setCpBusy(false));
                  }}
                >
                  {cpBusy ? 'Working…' : 'Generate New Passport'}
                </button>
              </div>
            </>
          }
        </div>

        {/* Notification preferences */}
        <div className="settings-section card">
          <div className="section-header">
            <Bell size={24} color="#E09B1A" />
            <h2>Notifications</h2>
          </div>
          <p className="settings-section-desc">Expiration and compliance alerts via email and SMS.</p>
          {prefsLoading ? (
            <p className="settings-muted">Loading preferences...</p>
          ) : prefs ? (
            <div className="notification-prefs">
              <div className="setting-item notification-toggle">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={!!prefs.email_enabled}
                    onChange={(e) => saveNotificationPrefs({ email_enabled: e.target.checked })}
                  />
                  <span>Email alerts</span>
                </label>
                <span className="setting-hint">Receive expiration and compliance reminders by email</span>
              </div>
              <div className="setting-item notification-toggle">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={!!prefs.sms_enabled}
                    onChange={(e) => saveNotificationPrefs({ sms_enabled: e.target.checked })}
                  />
                  <span>SMS alerts</span>
                </label>
                <span className="setting-hint">Receive critical reminders by text (Pro plans)</span>
              </div>
              <div className="setting-item">
                <label className="setting-label">Phone number (for SMS)</label>
                <div className="phone-row">
                  <input
                    type="tel"
                    className="input-field"
                    placeholder="+1 555 123 4567"
                    value={prefs.phone || ''}
                    onChange={(e) => setPrefs((p) => (p ? { ...p, phone: e.target.value } : p))}
                    onBlur={(e) => saveNotificationPrefs({ phone: e.target.value })}
                  />
                  <button type="button" className="button secondary" onClick={handleVerifyPhone}>
                    Verify
                  </button>
                </div>
                {verifyPhoneMessage && <p className="settings-hint-inline">{verifyPhoneMessage}</p>}
              </div>
              <div className="setting-item">
                <label className="setting-label">Lead time (alert me this many days before expiration)</label>
                <select
                  className="input-field"
                  value={prefs.lead_time_days}
                  onChange={(e) => saveNotificationPrefs({ lead_time_days: Number(e.target.value) })}
                >
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                </select>
              </div>
              <div className="setting-item quiet-hours">
                <label className="setting-label">Quiet hours (no notifications sent during this window)</label>
                <div className="quiet-hours-row">
                  <input
                    type="time"
                    className="input-field"
                    value={prefs.quiet_hours?.start || '22:00'}
                    onChange={(e) => saveNotificationPrefs({ quiet_hours_start: e.target.value })}
                  />
                  <span className="quiet-hours-sep">to</span>
                  <input
                    type="time"
                    className="input-field"
                    value={prefs.quiet_hours?.end || '07:00'}
                    onChange={(e) => saveNotificationPrefs({ quiet_hours_end: e.target.value })}
                  />
                </div>
                <span className="setting-hint">Server time (e.g. 10:00 PM – 7:00 AM)</span>
              </div>
              {prefsSaving && <p className="settings-muted">Saving...</p>}
            </div>
          ) : null}
        </div>

        {/* Billing Section */}
        <div className="settings-section card">
          <div className="section-header">
            <CreditCard size={24} color="#E09B1A" />
            <h2>Billing</h2>
          </div>
          
          <div className="billing-info">
            <div className="setting-item">
              <div>
                <h3>Payment Method</h3>
                <p>Manage payment methods in your subscription</p>
              </div>
              <button className="button secondary" onClick={() => navigate('/pricing')}>
                View plans
              </button>
            </div>
            
            <div className="setting-item">
              <div>
                <h3>Billing History</h3>
                <p>View past invoices and receipts</p>
              </div>
              <button className="button secondary">View History</button>
            </div>
          </div>
        </div>

        <div className="settings-section card settings-legal-section">
          <h2>Legal</h2>
          <div className="settings-legal-links">
            <Link to="/terms">Terms of Service</Link>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/support">Help</Link>
          </div>
        </div>

        {/* Danger Zone — account deletion request */}
        <div className="settings-section card settings-danger-zone">
          <h2 className="settings-danger-zone__title">Danger Zone</h2>

          {user?.deletionRequestedAt ? (
            <div className="settings-deletion-pending">
              <div className="settings-deletion-banner" role="status">
                ⚠ Account deletion requested on {formatDeletionDate(user.deletionRequestedAt)}.
                Your account will be deleted within 7 days.
              </div>
              <p className="settings-deletion-muted">
                Changed your mind? You can cancel this request before your account is deleted.
              </p>
              <button
                type="button"
                className="button settings-deletion-cancel-btn"
                disabled={deletionLoading}
                onClick={async () => {
                  setDeletionError('');
                  setDeletionLoading(true);
                  try {
                    await axios.post(
                      `${API_URL}/api/users/cancel-deletion`,
                      {},
                      getAuthHeaders(),
                    );
                    await refreshUserFromApi(setUser);
                  } catch (err) {
                    setDeletionError(
                      err.response?.data?.message ||
                        'Failed to cancel deletion request. Please try again.',
                    );
                  } finally {
                    setDeletionLoading(false);
                  }
                }}
              >
                {deletionLoading ? 'Cancelling…' : 'Cancel Deletion Request'}
              </button>
              {deletionError ? (
                <p className="settings-inline-error" style={{ marginTop: 12 }}>
                  {deletionError}
                </p>
              ) : null}
            </div>
          ) : (
            <div className="settings-deletion-default">
              <h3 className="settings-deletion-heading">Delete Account</h3>
              <p className="settings-deletion-muted">
                Request permanent deletion of your account and all associated data including
                documents, compliance history, and vehicle records. This cannot be undone.
              </p>
              <button
                type="button"
                className="button settings-deletion-request-btn"
                onClick={() => {
                  setDeletionError('');
                  setShowDeletionModal(true);
                }}
              >
                Request Account Deletion
              </button>
              {deletionError ? (
                <p className="settings-inline-error" style={{ marginTop: 12 }}>
                  {deletionError}
                </p>
              ) : null}
            </div>
          )}
        </div>
      </div>

      {showDeletionModal && (
        <div
          className="modal-overlay fleet-edit-modal-overlay settings-deletion-modal-overlay"
          role="presentation"
          onClick={() => !deletionLoading && setShowDeletionModal(false)}
        >
          <div
            className="vehicle-modal fleet-edit-modal settings-deletion-modal"
            role="dialog"
            aria-labelledby="deletion-modal-title"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header settings-deletion-modal__header">
              <h2 id="deletion-modal-title" className="modal-title">
                Are you sure?
              </h2>
              <button
                type="button"
                className="modal-close"
                aria-label="Close"
                disabled={deletionLoading}
                onClick={() => setShowDeletionModal(false)}
              >
                ×
              </button>
            </div>
            <div className="settings-deletion-modal__content">
              <p className="settings-deletion-modal__body">
                This will request deletion of your account and all associated data. You have 7
                days to cancel this request. After that, your account cannot be recovered.
              </p>
            </div>
            <div className="modal-actions settings-deletion-modal__actions">
              <button
                type="button"
                className="modal-button cancel"
                disabled={deletionLoading}
                onClick={() => setShowDeletionModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="modal-button settings-deletion-confirm-btn"
                disabled={deletionLoading}
                onClick={async () => {
                  setDeletionError('');
                  setDeletionLoading(true);
                  try {
                    await axios.post(
                      `${API_URL}/api/users/request-deletion`,
                      {},
                      getAuthHeaders(),
                    );
                    setShowDeletionModal(false);
                    await refreshUserFromApi(setUser);
                  } catch (err) {
                    setDeletionError(
                      err.response?.data?.message ||
                        'Failed to submit deletion request. Please try again.',
                    );
                  } finally {
                    setDeletionLoading(false);
                  }
                }}
              >
                {deletionLoading ? 'Submitting…' : 'Yes, request deletion'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
